import { useMutation } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';

import { paths } from '@constants/paths.ts';
import { DEPOSIT_AMOUNT } from '@constants/queryParams';
import { useSubmitRenewalCapture } from '@features/details/Details/RenewalCapture/useSubmitRenewalCapture';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useTrackingContext } from '@tracking/Provider/Context';
import {
  SignupCheckoutEntity,
  signupCheckoutEntity,
  useTrackSignupApplicationSubmitted,
} from '@tracking/trackingEvents.ts';

import { submitOrder as submitOrderQuery } from './query';

const useOrderSubmitter = () => {
  const navigate = useNavigate();
  const { basketId, handleApplicationSubmitted, setApplicationSubmitting } =
    useBasketContext();

  const trackApplicationSubmitted = useTrackSignupApplicationSubmitted();
  const { trackingContexts } = useTrackingContext();
  const [
    submitOrder,
    { loading: submitOrderLoading, error: submitOrderError },
  ] = useMutation(submitOrderQuery);
  const submitRenewalCapture = useSubmitRenewalCapture();

  const submitAndCompleteOrder = ({
    depositAmount,
    checkoutTrackingContext,
  }: {
    checkoutTrackingContext?: SignupCheckoutEntity;
    depositAmount?: string;
  }) => {
    setApplicationSubmitting(true);

    return submitOrder({
      variables: {
        input: {
          basketId,
        },
      },
      onCompleted: async () => {
        // TODO: Not all flows using this function have tracking implemented,
        //  namely deposit, can remove this check and optional parameter when implemented
        if (checkoutTrackingContext) {
          trackApplicationSubmitted(
            {},
            {
              contexts: [
                ...trackingContexts,
                signupCheckoutEntity(checkoutTrackingContext),
              ],
            }
          );
        } else {
          trackApplicationSubmitted(
            {},
            {
              contexts: trackingContexts,
            }
          );
        }

        try {
          await submitRenewalCapture();
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'useOrderSubmitter', step: 'renewalCapture' },
            extra: { basketId },
          });
        }

        handleApplicationSubmitted();

        navigate(paths.thankYou, {
          state: {
            [DEPOSIT_AMOUNT]: depositAmount,
          },
        });
      },
    });
  };

  return {
    submitAndCompleteOrder,
    submitOrderLoading,
    submitOrderError,
  };
};

export { useOrderSubmitter };
